// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node, useState } from 'react';
import { omit } from 'lodash';

import Icon from '@zapier/design-system/src/components/display/Icon';
import Link from '@zapier/design-system/src/components/navigation/Link';
import {
  Animation,
  type ColorValue,
  Colors,
  type IconName,
  Shadows,
  Typography,
} from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

type ButtonColorVariant = {
  base: {
    backgroundColor?: ColorValue,
    borderColor?: ColorValue,
    color?: ColorValue,
  },
  disabled: {
    backgroundColor?: ColorValue,
    borderColor?: ColorValue,
    color?: ColorValue,
  },
  hover: {
    backgroundColor?: ColorValue,
    borderColor?: ColorValue,
    color?: ColorValue,
  },
  focus: {
    backgroundColor?: ColorValue,
    borderColor?: ColorValue,
    outlineColor?: ColorValue,
    color?: ColorValue,
  },
  selected?: {
    backgroundColor?: ColorValue,
    borderColor?: ColorValue,
    color?: ColorValue,
  },
};

export const ButtonColors: { [string]: ButtonColorVariant } = {
  // Primary - Blue
  blue: {
    base: {
      backgroundColor: Colors.blue500,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.blue700,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.white,
    },
  },
  // Secondary - Black
  black: {
    base: {
      backgroundColor: Colors.black,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.grey900,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.black,
      outlineColor: Colors.grey700,
      color: Colors.white,
    },
  },
  // Secondary - Grey
  grey: {
    base: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.blue500,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.white,
    },
  },
  // Tertiary - Grey
  greyAlt: {
    base: {
      backgroundColor: Colors.grey100,
      color: Colors.black,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.black,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.black,
      outlineColor: Colors.black,
      color: Colors.white,
    },
    selected: {
      backgroundColor: Colors.black,
      color: Colors.white,
    },
  },
  orange: {
    base: {
      backgroundColor: Colors.red500,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.red600,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.red500,
      outlineColor: Colors.red500,
      color: Colors.white,
    },
  },
  // Tertiary - Stroke
  white: {
    base: {
      backgroundColor: Colors.white,
      color: Colors.black,
      borderColor: Colors.grey400,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      borderColor: 'transparent',
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.white,
      color: Colors.blue500,
      borderColor: Colors.grey400,
    },
    focus: {
      backgroundColor: Colors.white,
      borderColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.blue500,
    },
  },
};

type DerivedState = {
  isFocused: boolean,
  isHovered: boolean,
};

export type ButtonColor = $Keys<typeof ButtonColors>;

export type ButtonProps = {|
  /** Text label for the button or any custom Node, including functions. */
  children: Node | (DerivedState => Node),
  /** Changes button to a link. */
  href?: string,
  /** Change text to upper-case. */
  casing?: 'uppercase',
  /** Disable the button. */
  disabled?: boolean,
  /** Should the left, right or all sides be rounded? */
  borderRadiusSide?: 'left' | 'right',
  /** Callback to be executed when the button is clicked. */
  onClick?: (e: SyntheticMouseEvent<*>) => void,
  /** Link target */
  target?: '_self' | '_blank' | '_parent' | '_top',
  /** Link rel */
  rel?: string,
  /** DOM type of button tag. */
  type?: 'button' | 'submit' | 'reset',
  /** Color of the button. */
  color: ButtonColor,
  /**
   * Color themes for the button. The default value for this prop
   * should only be overridden in rare cases, and `color` should be
   * leveraged as much as possible instead.
   */
  colorTheme: {
    [string]: ButtonColorVariant,
  },
  /**
   * Optional `for` attribute to apply to node.
   * If supplied, the rendered tag will be a `label`.
   */
  htmlFor?: string,
  /** Optional icon to render before `children` */
  iconBefore?: IconName,
  /** Optional icon to render after `children` */
  iconAfter?: IconName,
  /** Indicates whether the button content is padded or not. */
  isPadded: boolean,
  /** Renders the button in a pill shaped format. */
  isPill?: boolean,
  /** Defines whether the button is selected. */
  isSelected?: boolean,
  /** Renders the button in a square, keeping height and width consistent. */
  isSquare?: boolean,
  /** Renders a drop-shadow behind the button */
  hasShadow?: boolean,
  /** Force button to take up full width of container. */
  isFullWidth?: boolean,
  /** The vertical size of the button */
  size: 'xsmall' | 'small' | 'medium',
  /** The id of the element that describes the button, used for aria-describedby*/
  ariaDescribedBy?: string,
  /** An optional aria label to describe the button */
  ariaLabel?: string,
  /** An optional aria-expanded attribute to describe if the button's expandable/collapsible content is expanded currently */
  ariaExpanded?: boolean,
  /** Optionally pass an ID to identify the what element the button controls (e.g. expands/collapses) */
  ariaControls?: string,
  /** An optional aria-haspopup attribute if this button triggers a popup/dropdown */
  ariaHaspopup?: boolean,
  /** An optional aria-hidden attribute if this button should not be available to screenreaders. Only use this to improve functionality for screenreaders, never to remove functionality! */
  ariaHidden?: boolean,
  /** Optionally pass an ID to identify the button */
  id?: string,
  /** When `true`, the button is rendered as a `<span>` rather than as a link or button. */
  presentationOnly?: boolean,
  onKeyDown?: (e: KeyboardEvent) => void,
|};

type ButtonIconProps = {
  iconName: IconName,
  position: 'before' | 'after',
  size: number,
};

const buttonHeights = {
  medium: 50,
  small: 40,
  xsmall: 30,
};

const getTag = (props: ButtonProps) => {
  if (props.presentationOnly) {
    return 'span';
  }
  if (props.htmlFor && !props.disabled) {
    return 'label';
  }
  if (props.href && !props.disabled) {
    return Link;
  }
  return 'button';
};

const Styles = labelStyles(__filename, {
  root: (props: ButtonProps) => [
    {
      alignItems: 'center',
      appearance: 'none',
      borderRadius: 10,
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: 'transparent',
      boxSizing: 'border-box',
      position: 'relative',
      cursor: 'pointer',
      display: 'inline-flex',
      ...Typography.base,
      fontWeight: 700,
      justifyContent: 'center',
      lineHeight: 1.3,
      margin: 0, // Safari applies 2px margin to buttons this clears it
      minWidth: 0,
      opacity: 1,
      textAlign: 'center',
      textDecoration: 'none',
      textTransform: 'none',
      transitionProperty: 'box-shadow, border-color, background-color',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      '&::-moz-focus-inner': {
        padding: 0,
        border: 0,
      },
      // This is the outline that renders around the button when
      // it's focused. It's a pseudo element to preserve transparency
      // between the edge of the button and the edge of the outline.
      // It's defined here vs only in `:focus` so that it can transition
      // between states.
      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        border: '2px solid transparent',
        borderRadius: 12,
        transitionProperty: 'all',
        transitionTimingFunction: Animation.transitionTimingFunction,
        transitionDuration: Animation.transitionDuration,
      },
    },

    withModifier(props.color, {
      ...props.colorTheme[props.color].base,
    }),

    props.isSelected &&
      withModifier('selected', {
        ...props.colorTheme[props.color].selected,
        // Hide `:focus` outline on selected buttons
        '&::before': {
          content: 'none',
        },
      }),

    withModifier(props.color, {
      '&:hover:not([disabled])': {
        ...props.colorTheme[props.color].hover,
      },
      '&:focus:not([disabled])': {
        ...omit(props.colorTheme[props.color].focus, 'outlineColor'),
        '&::before': {
          borderColor: props.colorTheme[props.color].focus.outlineColor,
          top: -5,
          right: -5,
          bottom: -5,
          left: -5,
        },
      },
    }),

    props.casing &&
      withModifier(props.casing, {
        textTransform: props.casing,
      }),

    props.disabled &&
      withModifier('disabled', {
        ...props.colorTheme[props.color].disabled,
        cursor: 'not-allowed',
      }),

    props.isFullWidth &&
      withModifier('fullWidth', {
        width: '100%',
      }),

    props.isPill &&
      withModifier('pill', {
        borderRadius: 100,
        '&::before': {
          borderRadius: 'inherit',
        },
      }),

    props.hasShadow &&
      withModifier('shadowed', {
        boxShadow: Shadows.elevation5,
      }),

    withModifier(props.size, {
      height: buttonHeights[props.size],
      fontSize: {
        xsmall: 12,
        small: 14,
        medium: 16,
      }[props.size],
    }),

    props.isSquare &&
      withModifier('square', {
        width: buttonHeights[props.size],
        fontSize: {
          xsmall: 14,
          small: 14,
          medium: 16,
        }[props.size],
      }),

    props.borderRadiusSide &&
      withModifier(`borderRadiusSide-${props.borderRadiusSide}`, {
        borderRadius: {
          left: '10px 0 0 10px',
          right: '0 10px 10px 0',
        }[props.borderRadiusSide],
        '&::before': {
          borderRadius: {
            left: '12px 0 0 12px',
            right: '0 12px 12px 0',
          }[props.borderRadiusSide],
        },
      }),
  ],
  buttonContent: props => [
    {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
    },
    withModifier(
      props.size,
      {
        xsmall: { padding: '0 20px' },
        small: { padding: '0 20px' },
        medium: { padding: '0 30px' },
      }[props.size]
    ),
    !props.isPadded &&
      withModifier('unpadded', {
        padding: 0,
      }),
    props.isSquare &&
      withModifier('square', {
        padding: 0,
      }),
  ],
  buttonText: {
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  buttonIcon: ({ position }: ButtonIconProps) => [
    {
      display: 'block',
      flex: 'none',
    },
    position === 'before' &&
      withModifier(position, {
        marginRight: 8,
      }),
    position === 'after' &&
      withModifier(position, {
        marginLeft: 8,
      }),
  ],
});

const sizeToIconSize = {
  xsmall: 18,
  small: 24,
  medium: 24,
};

const ButtonIcon = (props: ButtonIconProps) => (
  <span css={Styles.buttonIcon(props)}>
    <Icon
      canAcceptPointerEvents={false}
      isBlock={true}
      name={props.iconName}
      size={props.size}
    />
  </span>
);

const ButtonText = (props: { children: Node }) => (
  <span css={Styles.buttonText}>{props.children}</span>
);

const ButtonContent = (props: ButtonProps) => {
  return (
    <span css={Styles.buttonContent(props)}>
      {props.iconBefore && (
        <ButtonIcon
          iconName={props.iconBefore}
          position="before"
          size={sizeToIconSize[props.size]}
        />
      )}
      <ButtonText>{props.children}</ButtonText>
      {props.iconAfter && (
        <ButtonIcon
          iconName={props.iconAfter}
          position="after"
          size={sizeToIconSize[props.size]}
        />
      )}
    </span>
  );
};

/**
 * Classic button component. Use when providing calls to action to users.
 *
 * Tags: core-ui
 */
export const Button = (props: ButtonProps) => {
  const ButtonTag = getTag(props);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const {
    children,
    href,
    htmlFor,
    id,
    ariaLabel,
    target,
    rel,
    type,
    disabled,
    ariaDescribedBy,
    onClick,
    presentationOnly,
    onKeyDown,
    ariaExpanded,
    ariaControls,
    ariaHaspopup,
    ariaHidden,
  } = props;

  const role = disabled && href ? 'button' : undefined;
  const derivedState = {
    isFocused: !disabled && isFocused,
    isHovered: !disabled && isHovered,
  };

  // These properties are only applied if the rendered element is a `<button>`.
  const buttonProps = !presentationOnly
    ? {
        type,
      }
    : {};

  // These properties are only applied if the rendered element is an `<a>`.
  const linkProps =
    href && !disabled && !presentationOnly
      ? {
          ariaLabel,
          href,
          rel,
          target,
        }
      : {};

  return (
    <ButtonTag
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      css={Styles.root(props)}
      disabled={disabled}
      htmlFor={htmlFor}
      id={id}
      onBlur={() => setIsFocused(false)}
      onClick={onClick}
      onFocus={() => setIsFocused(true)}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={role}
      {...buttonProps}
      {...linkProps}
    >
      {typeof children === 'function' ? (
        children({ ...derivedState, ButtonContent, ButtonText, ButtonIcon })
      ) : (
        <ButtonContent {...props} />
      )}
    </ButtonTag>
  );
};

Button.defaultProps = {
  color: 'blue',
  colorTheme: ButtonColors,
  disabled: false,
  isFullWidth: false,
  isPadded: true,
  isPill: false,
  size: 'medium',
};

export default Button;
